/**
 * Mar Amoli — Card grid handlers (cart-aware)
 *
 * Behavior:
 *   - On load: fetch /cart.js, map variant_id → quantity in cart.
 *   - For each <article class="ma-card">:
 *       - If cart already has this variant: stepper inicia en esa qty,
 *         botón muestra "N en carrito" (estado muted, no acciona).
 *       - Si el usuario cambia el stepper a un número distinto: botón
 *         cambia a "Actualizar".
 *       - Si la card no estaba en carrito: botón "Agregar".
 *   - Click button:
 *       - "Agregar" (cartQty=0)        → POST /cart/add.js  (qty del stepper)
 *       - "Actualizar" (cartQty>0,!=)  → POST /cart/change.js (set absoluto)
 *       - "N en carrito" (cartQty=N)   → no-op
 *   - Después de cualquier mutación: re-fetch cart, re-sync todos los cards.
 *   - Listens for "ma:cart:changed" para refrescar cuando el drawer cambia.
 */
(function () {
  "use strict";

  let cartByVariant = new Map();

  // ── Helpers ────────────────────────────────────────────
  function renderDrawerSection(html) {
    const drawer = document.querySelector("ma-cart-drawer");
    if (!drawer) return;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const newDrawer = doc.querySelector("ma-cart-drawer");
    if (!newDrawer) return;
    drawer.replaceChildren(...Array.from(newDrawer.childNodes));
    drawer.querySelectorAll("[data-ma-drawer-close]").forEach((el) => {
      el.addEventListener("click", () => drawer.close && drawer.close());
    });
  }

  function pulseCartCount() {
    document.querySelectorAll("[data-ma-cart-count]").forEach((el) => {
      el.style.transition = "transform 200ms cubic-bezier(.22,.61,.36,1)";
      el.style.transform = "scale(1.4)";
      setTimeout(() => { el.style.transform = ""; }, 220);
    });
  }

  function updateCartCountElements(itemCount) {
    document.querySelectorAll("[data-ma-cart-count]").forEach((el) => {
      el.textContent = String(itemCount);
      el.dataset.maCartCount = String(itemCount);
      el.classList.toggle("is-zero", itemCount === 0);
    });
  }

  async function fetchCart() {
    try {
      const res = await fetch("/cart.js", { headers: { "Accept": "application/json" } });
      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      console.warn("[ma-card] fetchCart failed", err);
      return null;
    }
  }

  function indexCart(cart) {
    cartByVariant = new Map();
    if (!cart || !cart.items) return;
    for (const item of cart.items) {
      cartByVariant.set(String(item.variant_id), item.quantity);
    }
  }

  function arrowSvg() {
    const ns = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(ns, "svg");
    svg.setAttribute("width", "14");
    svg.setAttribute("height", "14");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("aria-hidden", "true");
    const path = document.createElementNS(ns, "path");
    path.setAttribute("d", "M5 12h14M13 5l7 7-7 7");
    svg.appendChild(path);
    return svg;
  }

  function setBtnLabel(btn, text, withArrow) {
    btn.replaceChildren(document.createTextNode(text));
    if (withArrow) {
      btn.appendChild(document.createTextNode(" "));
      btn.appendChild(arrowSvg());
    }
  }

  function applyButtonState(card, { initStepper = false } = {}) {
    const btn = card.querySelector("[data-ma-card-add]");
    if (!btn) return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    const input = card.querySelector(".ma-card__qty-input");
    const cartQty = cartByVariant.get(String(variantId)) || 0;

    if (initStepper && cartQty > 0 && input && !card.dataset.maCardSynced) {
      input.value = String(cartQty);
    }
    card.dataset.maCardSynced = "1";

    const stepperQty = Math.max(1, parseInt(input?.value || "1", 10));
    btn.dataset.cartQty = String(cartQty);

    btn.classList.remove("ma-card__atc--in-cart", "ma-card__atc--update");
    btn.disabled = false;

    if (cartQty === 0) {
      setBtnLabel(btn, "Agregar", true);
    } else if (cartQty === stepperQty) {
      btn.classList.add("ma-card__atc--in-cart");
      setBtnLabel(btn, `${cartQty} en carrito`, false);
    } else {
      btn.classList.add("ma-card__atc--update");
      const delta = stepperQty - cartQty;
      const sign = delta > 0 ? "+" : "−"; // U+2212 minus sign
      setBtnLabel(btn, `${sign}${Math.abs(delta)} Actualizar`, false);
    }
  }

  function syncAllCards() {
    document.querySelectorAll(".ma-card").forEach((card) => {
      applyButtonState(card, { initStepper: true });
    });
  }

  async function refreshCart() {
    const cart = await fetchCart();
    if (!cart) return;
    indexCart(cart);
    updateCartCountElements(cart.item_count);
    syncAllCards();
  }

  // ── Stepper +/- ────────────────────────────────────────
  document.addEventListener("click", (e) => {
    const stepBtn = e.target.closest("[data-qty-step]");
    if (!stepBtn) return;
    e.preventDefault();
    e.stopPropagation();
    const qtyWrap = stepBtn.closest("[data-ma-card-qty]");
    const input = qtyWrap?.querySelector(".ma-card__qty-input");
    if (!input) return;
    const step = parseInt(stepBtn.dataset.qtyStep, 10);
    const current = parseInt(input.value || "1", 10) || 1;
    input.value = Math.max(1, Math.min(99, current + step));
    const card = stepBtn.closest(".ma-card");
    if (card) applyButtonState(card);
  });

  document.addEventListener("input", (e) => {
    if (!e.target.matches(".ma-card__qty-input")) return;
    const card = e.target.closest(".ma-card");
    if (card) applyButtonState(card);
  });

  // ── Add / Update button ───────────────────────────────
  document.addEventListener("click", async (e) => {
    const btn = e.target.closest("[data-ma-card-add]");
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    if (btn.disabled) return;
    if (btn.classList.contains("ma-card__atc--in-cart")) {
      // Already in cart at this quantity — open drawer instead of no-op
      if (window.MaCart?.open) window.MaCart.open();
      return;
    }

    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    const card = btn.closest(".ma-card");
    const isIconOnly = !card; // botón "+" circular de recomendaciones del drawer
    const qtyInput = card?.querySelector(".ma-card__qty-input");
    const stepperQty = Math.max(1, parseInt(qtyInput?.value || "1", 10));
    const cartQty = cartByVariant.get(String(variantId)) || 0;
    const isUpdate = cartQty > 0;

    btn.disabled = true;
    if (!isIconOnly) {
      btn.replaceChildren(document.createTextNode(isUpdate ? "Actualizando…" : "Agregando…"));
    }

    try {
      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", String(stepperQty));
      // Marca de dónde salió la venta. Las propiedades que empiezan con "_"
      // no se le muestran al cliente, pero quedan en la línea del pedido: es
      // la única forma de saber después qué se vendió desde el carrito y qué
      // desde una ficha, y así decidir qué vale la pena impulsar ahí.
      const origen = btn.closest("[data-ma-rec-pane], [data-ma-rec-mobile]")
        ? "carrito-recomendacion"
        : btn.closest(".ma-aroma")
          ? "ficha-mismo-aroma"
          : null;
      if (origen && !isUpdate) {
        formData.append("properties[_origen]", origen);
      }
      formData.append("sections", "cart-drawer");
      formData.append("sections_url", window.location.pathname);
      const endpoint = isUpdate ? "/cart/change.js" : "/cart/add.js";
      const res = await fetch(endpoint, {
        method: "POST",
        body: formData,
        headers: { "Accept": "application/json" },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || "Error en el carrito");
      }
      const result = await res.json();
      if (result.sections && result.sections["cart-drawer"]) {
        // Re-render selectivo (preserva las recomendaciones ya pobladas) si el
        // componente del drawer lo expone; si no, reemplazo completo.
        if (window.MaCart && typeof window.MaCart.renderSection === "function") {
          window.MaCart.renderSection(result.sections["cart-drawer"], result.item_count);
        } else {
          renderDrawerSection(result.sections["cart-drawer"]);
        }
      }
      // Refresh cart cache (item_count needed for badges; /cart/add.js doesn't return it)
      await refreshCart();
      if (window.MaCart?.agregado) window.MaCart.agregado(); else if (window.MaCart?.open) window.MaCart.open();
      pulseCartCount();

      if (isIconOnly) {
        btn.replaceChildren(document.createTextNode("✓"));
        setTimeout(() => {
          btn.replaceChildren(document.createTextNode("+"));
          btn.disabled = false;
        }, 1100);
      } else {
        btn.replaceChildren(document.createTextNode(isUpdate ? "✓ Actualizado" : "✓ Agregado"));
        setTimeout(() => { applyButtonState(card); }, 1100);
      }
    } catch (err) {
      console.error("[ma-card] cart op failed", err);
      if (isIconOnly) {
        btn.replaceChildren(document.createTextNode("+"));
        btn.disabled = false;
      } else {
        btn.replaceChildren(document.createTextNode("Reintentar"));
        setTimeout(() => { applyButtonState(card); }, 1500);
      }
    }
  });

  // ── External cart-change events (drawer remove/edit) ──
  window.addEventListener("ma:cart:changed", refreshCart);

  // ── Initial sync ──────────────────────────────────────
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", refreshCart);
  } else {
    refreshCart();
  }
})();
